import type { BodyTuning, PushMode } from '../depth/body'

/**
 * Everything the config panel can change, and the tuning derived from it.
 *
 * `DemoConfig` is replaced rather than mutated: it changes only when a button
 * is pressed, so a fresh object per change costs nothing and keeps the flow of
 * state through the demo one-directional.
 */

/**
 * `hit` — the floor is ARCore's own tracked plane, from WebXR hit-test, and
 * depth keeps only cells standing proud of it. This is the architecture every
 * shipped implementation converged on, and it makes the floor flat by
 * construction rather than by filtering.
 *
 * `flat` — the floor is fitted from the depth frame itself and nearby cells are
 * snapped onto it. Kept because it is the only mode that measures the sensed
 * floor rather than replacing it.
 *
 * `raw` — no plane at all. The floor on screen is exactly what was sensed.
 */
export type FloorMode = 'hit' | 'flat' | 'raw'
export const FLOOR_MODES: readonly FloorMode[] = ['hit', 'flat', 'raw']

export type SkinMode = 'off' | 'wire' | 'solid'
export const SKIN_MODES: readonly SkinMode[] = ['off', 'wire', 'solid']

export const PUSH_MODES: readonly PushMode[] = ['off', 'resolve', 'shove']

/**
 * Box edge lengths. Half of each is the sensed-obstacle probe radius, so the
 * smallest still spans enough grid cells to fill the ring at stride 2.
 */
export const CUBE_SIZES: readonly number[] = [0.04, 0.06, 0.08]

/** Every box costs two full grid scans per substep. */
export const MAX_BODIES = 8

export interface DemoConfig {
  readonly sizeIndex: number
  readonly push: PushMode
  readonly floor: FloorMode
  readonly skin: SkinMode
  readonly stats: boolean
  /** `Sim: run` integrates velocities; `Sim: hold` keeps every box parked. */
  readonly running: boolean
}

export const DEFAULT_CONFIG: DemoConfig = {
  sizeIndex: 1,
  push: 'off',
  floor: 'hit',
  skin: 'off',
  stats: true,
  running: false,
}

/** Slope subtracted from every support fit, so a tilted table can be zeroed. */
export interface SlopeBias {
  readonly x: number
  readonly z: number
}

export const NO_BIAS: SlopeBias = { x: 0, z: 0 }

const BASE_TUNING = {
  gravity: 2.5,
  staticSlope: 0.18,
  kineticMu: 0.14,
  damping: 0.4,
  climbMetres: 0.008,
  supportRadius: 0.1,
  probeDirections: 16,
  maxPushSpeed: 0.8,
  maxCorrection: 0.015,
  substepSeconds: 1 / 120,
} as const

export function buildTuning(config: DemoConfig, bias: SlopeBias): BodyTuning {
  return {
    ...BASE_TUNING,
    pushMode: config.push,
    frozen: !config.running,
    biasSlopeX: bias.x,
    biasSlopeZ: bias.z,
  }
}

export function cubeSize(config: DemoConfig): number {
  return CUBE_SIZES[config.sizeIndex] ?? CUBE_SIZES[0]!
}

/** Advances a cycling control, wrapping at the end. */
export function next<T>(values: readonly T[], current: T): T {
  const index = values.indexOf(current)
  return values[(index + 1) % values.length] ?? current
}
